import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { artUrl } from "@/lib/uploadArt";
import { useTour } from "@/context/TourContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { HelpHint } from "@/components/HelpHint";
import {
  Wand2, ArrowLeft, Scissors, Ruler, LayoutTemplate, Type, SlidersHorizontal,
  Sparkles, CheckCircle2, Lightbulb, FileDown, Trash2, NotebookPen,
} from "lucide-react";

const IMG = "https://static.prod-images.emergentagent.com/jobs/e6db9e08-0442-4577-a975-bcd45ac40566/images/";
const IMAGES = {
  hero: IMG + "ef5fc8578cf9f4531b1970367a780bdc66da4083f3aadab99dba6a82fd88024d.jpeg",
  fillFitStretch: IMG + "98740d14235b4adf4a19bfb1f78444b9eaaec8ac36a8f26a81a926cf7ed72678.jpeg",
  cutBleed: IMG + "4a6738608b1cdaec3e0067ec437f48877465c47324abfa94e30ef75cc53a84ca.jpeg",
  templates: IMG + "563434ec52b2e029bca67f63737c20f1095d2505c13777ca52bdcc7db902890b.jpeg",
  customize: IMG + "b9840fd3d30272ff269bc008ce98a6aa4f9678bc138565b5e04991322e7a262f.jpeg",
  filters: IMG + "49559999982ac36c213e475d39b3918f586f9e18f76deabfa492b9e368d056d0.jpeg",
  aiStudio: IMG + "a2ba047877626cbc491598a8dac027fbbe49c5a9dd6939980eb00127d6bebf98.jpeg",
  fitToSize: IMG + "9e4e26623cc0c81a5114fccec4bfaf208906a0ac745d6d7dd7afc4b4b677933a.jpeg",
};

// Each section: bilingual [EN, ES] tuples so it follows the app language toggle.
const SECTIONS = [
  {
    id: "cut-bleed", icon: Scissors, image: IMAGES.cutBleed,
    tab: ["Cut & Bleed tab", "Pestaña Cut & Bleed"],
    title: ["Cut & Bleed", "Corte y Sangrado"],
    what: [
      "Turn a single client image into a VersaWorks-ready PDF with bleed and a CutContour cut line — for stickers, decals and die-cut shapes.",
      "Convierte una sola imagen del cliente en un PDF listo para VersaWorks con sangrado y una línea de corte CutContour — para stickers, calcomanías y formas troqueladas.",
    ],
    steps: [
      ["Upload the artwork (PNG with transparent background traces cleanest).", "Sube el arte (PNG con fondo transparente traza más limpio)."],
      ["Pick a cut mode (shape / rectangle) and the final size.", "Elige el modo de corte (forma / rectángulo) y el tamaño final."],
      ["The system adds bleed and the magenta CutContour spot color automatically.", "El sistema agrega el sangrado y el color directo CutContour magenta automáticamente."],
      ["Download the production PDF and send it to the RIP.", "Descarga el PDF de producción y envíalo al RIP."],
    ],
    tip: ["Bleed is the extra edge that gets trimmed off — it prevents white borders after cutting.", "El sangrado es el borde extra que se recorta — evita bordes blancos después del corte."],
  },
  {
    id: "fit-to-size", icon: Ruler, image: IMAGES.fitToSize, extra: IMAGES.fillFitStretch,
    tab: ["Fit to Size tab", "Pestaña Fit to Size"],
    title: ["Fit to Size", "Ajustar a Tamaño"],
    highlight: true,
    what: [
      "Upload any image and type the exact print size you want — no template needed. The image auto-adjusts and exports a production-ready PDF at that exact size.",
      "Sube cualquier imagen y escribe el tamaño de impresión exacto que quieres — sin necesidad de plantilla. La imagen se auto-ajusta y exporta un PDF listo para producción a ese tamaño exacto.",
    ],
    steps: [
      ["Upload the image (PNG / JPG / PDF, up to 150 MB).", "Sube la imagen (PNG / JPG / PDF, hasta 150 MB)."],
      ["Type the Width and Height, choose the Unit (in / cm / mm / ft).", "Escribe el Ancho y Alto, elige la Unidad (in / cm / mm / ft)."],
      ["Optionally add Bleed (leave 0 for exact size).", "Opcionalmente agrega Sangrado (deja 0 para tamaño exacto)."],
      ["Choose the Adjust mode (see the three modes below).", "Elige el modo de Ajuste (ve los tres modos abajo)."],
      ["Press 'Build print-ready PDF' and download.", "Presiona 'Build print-ready PDF' y descarga."],
    ],
    modes: [
      ["Fill (crop)", "Llenar (recortar)", "Covers the whole size and crops the overflow. Best for photos and banners.", "Cubre todo el tamaño y recorta lo que sobra. Ideal para fotos y banners."],
      ["Fit (letterbox)", "Contener (margen)", "Shows the whole image with white margins if the shape doesn't match.", "Muestra la imagen completa con márgenes blancos si la forma no coincide."],
      ["Stretch", "Estirar", "Forces the image to the exact size (may distort proportions).", "Fuerza la imagen al tamaño exacto (puede distorsionar las proporciones)."],
    ],
    tip: ["Example: a 24\"×48\" horizontal photo → set Width 48, Height 24, mode Fill. A 24\"×36\" vertical → Width 24, Height 36.", "Ejemplo: una foto horizontal 24\"×48\" → pon Ancho 48, Alto 24, modo Llenar. Una vertical 24\"×36\" → Ancho 24, Alto 36."],
  },
  {
    id: "templates", icon: LayoutTemplate, image: IMAGES.templates,
    tab: ["Templates tab", "Pestaña Templates"],
    title: ["Templates", "Plantillas"],
    what: [
      "Build and save a reusable library of your production templates: size, bleed, sides and slots. Once saved, drop any client file into them instantly.",
      "Crea y guarda una biblioteca reutilizable de tus plantillas de producción: tamaño, sangrado, lados y espacios (slots). Una vez guardadas, coloca cualquier archivo del cliente en ellas al instante.",
    ],
    steps: [
      ["Click 'Add template' and set name, trim size, bleed and sides.", "Haz clic en 'Add template' y define nombre, tamaño de corte, sangrado y lados."],
      ["Optionally define slots (positions where artwork is placed).", "Opcionalmente define slots (posiciones donde se coloca el arte)."],
      ["Save — it is now available in Fit to Template.", "Guarda — ya está disponible en Fit to Template."],
    ],
    tip: ["Use templates for jobs you repeat often (e.g. a standard 24×36 poster or a 2-sided card).", "Usa plantillas para trabajos que repites seguido (ej. un póster estándar 24×36 o una tarjeta a 2 caras)."],
  },
  {
    id: "fit-to-template", icon: LayoutTemplate, image: null,
    tab: ["Fit to Template tab", "Pestaña Fit to Template"],
    title: ["Fit to Template", "Ajustar a Plantilla"],
    what: [
      "Drop a client's ready file into one of your saved templates. Artwork is auto-rotated per slot, bleed is included and guides are removed.",
      "Coloca el archivo listo del cliente en una de tus plantillas guardadas. El arte se rota automáticamente por slot, se incluye el sangrado y se quitan las guías.",
    ],
    steps: [
      ["Pick a template from the dropdown.", "Elige una plantilla del menú."],
      ["Upload the client's ready PDF/image.", "Sube el PDF/imagen listo del cliente."],
      ["Press 'Build print-ready PDF' and download.", "Presiona 'Build print-ready PDF' y descarga."],
    ],
    tip: ["For a 2-page file, use a 2-side template so both sides map correctly.", "Para un archivo de 2 páginas, usa una plantilla de 2 caras para que ambos lados se asignen bien."],
  },
  {
    id: "customize", icon: Type, image: IMAGES.customize,
    tab: ["Customize tab", "Pestaña Customize"],
    title: ["Customize", "Personalizar"],
    what: [
      "Render editable Text and QR codes on top of a template background (ingested from PSD/AI flattened files). Great for name badges, event signage or QR flyers.",
      "Renderiza Texto editable y códigos QR sobre el fondo de una plantilla (importado de archivos PSD/AI aplanados). Ideal para gafetes, señalización de eventos o flyers con QR.",
    ],
    steps: [
      ["Ingest a design (upload the flattened background image).", "Importa un diseño (sube la imagen de fondo aplanada)."],
      ["Fill in the editable fields (text, QR content).", "Llena los campos editables (texto, contenido del QR)."],
      ["Generate and download the rendered file.", "Genera y descarga el archivo renderizado."],
    ],
    tip: ["QR codes are generated live from the value you type — test them before printing.", "Los códigos QR se generan en vivo con el valor que escribes — pruébalos antes de imprimir."],
  },
  {
    id: "filters", icon: SlidersHorizontal, image: IMAGES.filters,
    tab: ["Filters tab", "Pestaña Filters"],
    title: ["Filters", "Filtros"],
    what: [
      "Apply instant, free photo filters (B&W, Sepia, Vintage, Duotone, Blur, Mosaic, and more) with an intensity slider and a Before/After comparison. No AI credits used.",
      "Aplica filtros de foto instantáneos y gratis (B&N, Sepia, Vintage, Duotono, Desenfoque, Mosaico y más) con un deslizador de intensidad y comparación Antes/Después. No usa créditos de IA.",
    ],
    steps: [
      ["Upload a photo.", "Sube una foto."],
      ["Click a filter and adjust the Intensity slider.", "Haz clic en un filtro y ajusta el deslizador de Intensidad."],
      ["Drag the Before/After slider, then download.", "Arrastra el comparador Antes/Después y descarga."],
    ],
    tip: ["These are deterministic image filters (instant) — artistic AI styles live in the AI Studio tab.", "Son filtros de imagen deterministas (instantáneos) — los estilos artísticos con IA están en la pestaña AI Studio."],
  },
  {
    id: "ai-studio", icon: Sparkles, image: IMAGES.aiStudio,
    tab: ["AI Studio tab", "Pestaña AI Studio"],
    title: ["AI Studio (Manotas)", "AI Studio (Manotas)"],
    what: [
      "Generate stylized advertising product photos ('Manotas') with AI. Pick a base style from the catalog, describe the product, and generate. Quick corrections let you fix fingers, remove arms or add a contour.",
      "Genera fotos de producto publicitarias estilizadas ('Manotas') con IA. Elige un estilo base del catálogo, describe el producto y genera. Las correcciones rápidas te permiten arreglar dedos, quitar brazos o agregar contorno.",
    ],
    steps: [
      ["(Optional) Upload a product reference or pick a catalog style.", "(Opcional) Sube una referencia del producto o elige un estilo del catálogo."],
      ["Describe the product and choose position / hand style.", "Describe el producto y elige la posición / estilo de mano."],
      ["Generate, then use the quick corrections if needed.", "Genera y usa las correcciones rápidas si es necesario."],
    ],
    tip: ["AI generation consumes credits. Use Trial Mode to upload manual generations without spending credits.", "La generación con IA consume créditos. Usa el Modo Prueba (Trial) para subir generaciones manuales sin gastar créditos."],
  },
];

const Bullet = ({ children, testid }) => (
  <li className="flex gap-2 text-sm text-slate-700" data-testid={testid}>
    <CheckCircle2 size={16} className="text-[#2495D3] shrink-0 mt-0.5" />
    <span>{children}</span>
  </li>
);

const SECTION_TAB = {
  "cut-bleed": "prep", "fit-to-size": "fitsize", "templates": "templates",
  "fit-to-template": "fit", "customize": "customize", "filters": "filters", "ai-studio": "ai",
};

export default function PrintStudioManual() {
  const { lang } = useTour();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const t = (en, es) => (lang === "en" ? en : es);
  const [notes, setNotes] = useState([]);
  useEffect(() => { api.get("/studio/manual-notes").then((r) => setNotes(r.data)).catch(() => {}); }, []);
  const delNote = async (id) => {
    try { await api.delete(`/studio/manual-notes/${id}`); setNotes((n) => n.filter((x) => x.id !== id)); } catch { /* noop */ }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="print-studio-manual-page">
      <Link to="/production-file" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#2495D3] mb-5" data-testid="manual-back-link">
        <ArrowLeft size={16} /> {t("Back to Print Studio", "Volver a Print Studio")}
      </Link>

      {/* Hero */}
      <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white mb-10">
        <div className="grid md:grid-cols-2">
          <div className="p-8 flex flex-col justify-center">
            <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2">
              <Wand2 size={14} className="text-[#2495D3]" /> {t("User Manual", "Manual de Uso")}
            </p>
            <h1 className="font-head text-4xl font-black leading-tight mb-3">{t("Print Studio", "Print Studio")}</h1>
            <p className="text-slate-600 text-base leading-relaxed">
              {t(
                "Your hub to turn client files into print-ready output. This guide walks through every tab with examples and tips.",
                "Tu centro para convertir archivos del cliente en salidas listas para imprimir. Esta guía recorre cada pestaña con ejemplos y consejos."
              )}
            </p>
            <Link to="/production-file" className="mt-5">
              <Button className="bg-[#2495D3] hover:bg-[#1E7AA9]" data-testid="manual-open-studio">
                {t("Open Print Studio", "Abrir Print Studio")}
              </Button>
            </Link>
          </div>
          <div className="bg-slate-50 min-h-[220px]">
            <img src={IMAGES.hero} alt="Print Studio overview" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* Quick nav */}
      <div className="flex flex-wrap gap-2 mb-10" data-testid="manual-quicknav">
        {SECTIONS.map((s) => (
          <a key={s.id} href={`#${s.id}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-[#2495D3] hover:text-[#2495D3] transition-colors">
            {t(s.title[0], s.title[1])}
          </a>
        ))}
      </div>

      {/* Sections */}
      <div className="space-y-14">
        {SECTIONS.map((s, i) => {
          const Icon = s.icon;
          const flip = i % 2 === 1;
          return (
            <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`manual-section-${s.id}`}>
              <div className={`grid gap-8 items-start ${s.image ? "md:grid-cols-2" : ""}`}>
                {s.image && (
                  <div className={`rounded-xl overflow-hidden border border-slate-200 bg-slate-50 ${flip ? "md:order-2" : ""}`}>
                    <img src={s.image} alt={t(s.title[0], s.title[1])} className="w-full h-auto" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.highlight ? "bg-[#2495D3] text-white" : "bg-[#2495D3]/10 text-[#2495D3]"}`}>
                      <Icon size={18} />
                    </span>
                    <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">{t(s.tab[0], s.tab[1])}</span>
                  </div>
                  <h2 className="font-head text-2xl font-black mb-2 flex items-center gap-2">
                    {t(s.title[0], s.title[1])}
                    {s.highlight && <span className="text-[10px] font-mono uppercase bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">{t("New", "Nuevo")}</span>}
                  </h2>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{t(s.what[0], s.what[1])}</p>

                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2">{t("Steps", "Pasos")}</p>
                  <ol className="space-y-1.5 mb-4">
                    {s.steps.map((st, k) => <Bullet key={k} testid={`manual-${s.id}-step-${k}`}>{t(st[0], st[1])}</Bullet>)}
                  </ol>

                  {s.tip && (
                    <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                      <Lightbulb size={16} className="shrink-0 mt-0.5 text-amber-500" />
                      <span>{t(s.tip[0], s.tip[1])}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Adjust modes sub-block (Fit to Size) */}
              {s.modes && (
                <div className="mt-6 grid md:grid-cols-3 gap-4">
                  {s.extra && (
                    <div className="md:col-span-3 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                      <img src={s.extra} alt="Fill vs Fit vs Stretch" className="w-full h-auto" />
                    </div>
                  )}
                  {s.modes.map((m, k) => (
                    <div key={k} className="rounded-xl border border-slate-200 p-4 bg-white" data-testid={`manual-mode-${k}`}>
                      <div className="font-semibold text-sm text-[#2495D3] mb-1">{t(m[0], m[1])}</div>
                      <p className="text-sm text-slate-600">{t(m[2], m[3])}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Shop notes captured from Print Studio tabs */}
              {notes.filter((n) => n.tab === SECTION_TAB[s.id]).length > 0 && (
                <div className="mt-6" data-testid={`manual-notes-${s.id}`}>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5"><NotebookPen size={13} /> {t("Shop notes", "Notas del taller")}</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {notes.filter((n) => n.tab === SECTION_TAB[s.id]).map((n) => (
                      <div key={n.id} className="rounded-xl border border-slate-200 p-4 bg-white relative" data-testid={`manual-note-card-${n.id}`}>
                        {isAdmin && <button onClick={() => delNote(n.id)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500" data-testid={`manual-note-delete-${n.id}`}><Trash2 size={14} /></button>}
                        {n.title && <div className="font-semibold text-sm mb-1 pr-6">{n.title}</div>}
                        {n.note && <p className="text-sm text-slate-600 whitespace-pre-wrap mb-2">{n.note}</p>}
                        {n.images?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {n.images.map((im, k) => (
                              <a key={k} href={artUrl(im.file_id)} target="_blank" rel="noreferrer">
                                <img src={artUrl(im.file_id)} alt="" className="w-24 h-24 object-cover rounded border" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Footer CTA */}
      <div className="mt-14 rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
        <FileDown size={28} className="mx-auto text-[#2495D3] mb-2" />
        <h3 className="font-head text-xl font-black mb-1">{t("Ready to produce?", "¿Listo para producir?")}</h3>
        <p className="text-sm text-slate-600 mb-4">{t("Jump into Print Studio and turn your next client file into a print-ready PDF.", "Entra a Print Studio y convierte tu próximo archivo del cliente en un PDF listo para imprimir.")}</p>
        <Link to="/production-file">
          <Button className="bg-[#2495D3] hover:bg-[#1E7AA9]" data-testid="manual-footer-cta">{t("Open Print Studio", "Abrir Print Studio")}</Button>
        </Link>
      </div>
    </div>
  );
}
